/**
 * CGM-ROUBIA-LIVE-CONTROL-R1 — semantic multi-actuator pump controls.
 *
 * A relay_4ch node may drive more than one independently commissioned contactor
 * input. The browser never submits a relay channel: it selects a canonical
 * pump_id and the backend resolves pump_id -> relay_node_id / relay_channel.
 *
 * Relay contact state is observational evidence only; it is never described as
 * proof that a motor shaft is turning.
 */

import React, { useEffect, useMemo, useState } from 'react';
import { Info, Power } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { canIssuePhysicalCommands } from '../auth/cognito';
import { useAuth } from '../contexts/AuthContext';
import { useFieldBindings } from '../hooks/useFieldBindings';
import { usePumpCommand } from '../hooks/usePumpCommand';
import type { IoTNode, PumpBindingView } from '../lib/iotApi';
import { getRelayChannelState } from '../lib/liveSensorMetrics';
import { FRESHNESS_MS, nodeTimestamp } from '../lib/operationalOverview';
import { isTerminalCommandStatus } from '../lib/valveCommandSafety';
import { relativeAge } from '../lib/operationalFormat';

interface PumpActuatorControlsProps {
  node: IoTNode | undefined;
  siteName?: string;
}

const PumpActuatorControls: React.FC<PumpActuatorControlsProps> = ({ node, siteName }) => {
  const { t } = useTranslation('agrisafeR1');
  const { user } = useAuth();
  const { pumps, loaded } = useFieldBindings();
  const command = usePumpCommand(user?.role);
  const [, tick] = useState(0);

  useEffect(() => {
    const timer = window.setInterval(() => tick((value) => value + 1), 10000);
    return () => window.clearInterval(timer);
  }, []);

  const actuators = useMemo(
    () => node
      ? pumps.filter(
        (pump) => (!siteName || pump.site === siteName) && pump.relay_node_id === node.id
          && typeof pump.relay_channel === 'string'
          && pump.relay_channel.trim().length > 0,
      )
      : [],
    [node, pumps, siteName],
  );

  if (!node || !loaded || actuators.length === 0) return null;

  const now = Date.now();
  const ts = nodeTimestamp(node);
  const relayFresh = ts !== null && now - ts < FRESHNESS_MS;
  const canControlRole = canIssuePhysicalCommands(user?.role);

  const contactState = (pump: PumpBindingView): boolean | null => {
    if (!relayFresh || !pump.relay_channel) return null;
    return getRelayChannelState(node, pump.relay_channel);
  };

  const contactLabel = (value: boolean | null): string => {
    if (value === null) return t('pumping.relay.unknown');
    return value ? t('pumping.relay.on') : t('pumping.relay.off');
  };

  return (
    <section
      className="rounded-2xl border border-[var(--color-primary)]/25 bg-[var(--surface)] p-4 shadow-sm sm:p-5"
      data-testid="pump-actuator-controls"
      aria-label={t('pumping.commands.title')}
    >
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <div className="flex items-center gap-2">
            <Power className="h-5 w-5 text-[var(--color-primary)]" aria-hidden="true" />
            <h2 className="font-semibold text-[var(--text-primary)]">
              {t('pumping.commands.title')}
            </h2>
          </div>
          <p className="mt-1 text-xs text-[var(--text-secondary)]">
            {node.id} · {t('dashboard.lastData')} {relativeAge(ts, now, t)}
          </p>
        </div>
        <p className="inline-flex max-w-xl items-start gap-2 rounded-xl border border-[var(--border-light)] bg-[var(--bg-tertiary)] px-3 py-2 text-xs text-[var(--text-secondary)]">
          <Info className="mt-0.5 h-3.5 w-3.5 shrink-0 text-[var(--color-info)]" aria-hidden="true" />
          {t('pumping.evidence.relayContact')}
        </p>
      </div>

      <div className="mt-4 grid grid-cols-1 gap-3 lg:grid-cols-2">
        {actuators.map((pump) => {
          const contact = contactState(pump);
          const status = command.statusFor(pump.pump_id);
          const outcome = command.outcomeFor(pump.pump_id);
          const authorized = pump.command_authorized && canControlRole;
          const commandResolved = !status
      || isTerminalCommandStatus(status);
          const commandUnlocked = authorized
            && commandResolved
            && outcome === null
            && command.pending === null;
          return (
            <article
              key={pump.pump_id}
              className="rounded-xl border border-[var(--border-light)] bg-[var(--bg-tertiary)] p-4"
              data-testid={`pump-actuator-${pump.pump_id}`}
            >
              <div className="flex flex-wrap items-start justify-between gap-3">
                <div className="min-w-0">
                  <h3 className="truncate font-semibold text-[var(--text-primary)]">
                    {pump.function}
                  </h3>
                  <p className="mt-1 text-xs text-[var(--text-secondary)]">
                    {pump.relay_node_id} · {pump.relay_channel}
                  </p>
                </div>
                <span
                  className="rounded-full border border-[var(--border-light)] bg-[var(--surface)] px-2.5 py-1 text-xs font-semibold text-[var(--text-primary)]"
                  data-testid={`pump-actuator-state-${pump.pump_id}`}
                >
                  {contactLabel(contact)}
                </span>
              </div>

              {status && (
                <p
                  className="mt-2 text-xs font-medium text-[var(--text-secondary)]"
                  data-testid={`pump-actuator-command-status-${pump.pump_id}`}
                >
                  {status}
                </p>
              )}

              <div className="mt-3 grid grid-cols-2 gap-2">
                <button
                  type="button"
                  onClick={() => command.requestCommand(pump, 'start')}
                  disabled={!commandUnlocked}
                  data-testid={`pump-actuator-start-${pump.pump_id}`}
                  className="min-h-[48px] rounded-xl bg-[var(--color-success)] px-3 text-sm font-semibold text-white transition hover:opacity-90 disabled:cursor-not-allowed disabled:opacity-40"
                >
                  {t('dashboard.pump.start')}
                </button>
                <button
                  type="button"
                  onClick={() => command.requestCommand(pump, 'stop')}
                  disabled={!commandUnlocked}
                  data-testid={`pump-actuator-stop-${pump.pump_id}`}
                  className="min-h-[48px] rounded-xl bg-[var(--color-danger)] px-3 text-sm font-semibold text-white transition hover:opacity-90 disabled:cursor-not-allowed disabled:opacity-40"
                >
                  {t('dashboard.pump.stop')}
                </button>
              </div>

              {!commandUnlocked && (
                <p
                  className="mt-2 text-xs text-[var(--color-warning)]"
                  data-testid={`pump-actuator-lock-${pump.pump_id}`}
                >
                  {!canControlRole ? t('pumping.livePage.roleDenied')
                    : !pump.command_authorized ? t('dashboard.pump.lockedReason')
                      : outcome === 'conflict' ? t('pumping.livePage.conflict')
                        : outcome === 'forbidden' ? t('pumping.livePage.forbidden')
                          : outcome === 'uncertain' || status === 'UNCERTAIN' || status === 'TIMEOUT'
                            ? t('pumping.livePage.uncertain') : t('parcel.status.busy')}
                </p>
              )}
              {command.canRecheck?.(pump.pump_id) && (
                <button type="button" className="mt-2 min-h-[44px] underline"
                  onClick={() => command.recheckCommand(pump)} data-testid={`pump-recheck-${pump.pump_id}`}>
                  {t('common:refresh')}
                </button>
              )}
            </article>
          );
        })}
      </div>

      {command.pending && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4"
          role="dialog"
          aria-modal="true"
          data-testid="pump-actuator-confirm-dialog"
        >
          <div className="w-full max-w-md rounded-2xl bg-[var(--surface)] p-5 shadow-lg">
            <h2 className="text-lg font-semibold text-[var(--text-primary)]">
              {command.pending.action === 'start'
                ? t('dashboard.pump.confirmStartTitle')
                : t('dashboard.pump.confirmStopTitle')}
            </h2>
            <p className="mt-2 text-sm text-[var(--text-primary)]">
              {command.pending.pump.function} · {command.pending.pump.relay_channel}
            </p>

            {command.pending.requiresHydraulicConfirmation && (
              <div className="mt-3 rounded-xl border border-amber-200 bg-amber-50 px-3 py-2 text-xs text-amber-900">
                <div className="font-semibold">{t('dashboard.pump.manualHydraulicTitle')}</div>
                <div className="mt-1">{t('dashboard.pump.manualHydraulicConfirmation')}</div>
              </div>
            )}

            <div className="mt-5 flex justify-end gap-2">
              <button
                type="button"
                onClick={command.cancelConfirmation}
                className="min-h-[44px] rounded-xl border border-[var(--border-medium)] px-4 py-2 text-sm font-medium text-[var(--text-primary)]"
              >
                {t('dashboard.pump.cancel')}
              </button>
              <button
                type="button"
                onClick={command.confirmCommand}
                className="min-h-[44px] rounded-xl bg-[var(--color-primary)] px-4 py-2 text-sm font-semibold text-white"
              >
                {t('dashboard.pump.confirm')}
              </button>
            </div>
          </div>
        </div>
      )}
    </section>
  );
};

export default PumpActuatorControls;
